import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import { usePageSEODefaults } from '@/lib/seoHelpers';
import { generatePageSchema } from '@/lib/schemaHelpers';
import { getPerformanceHints } from '@/lib/seoHelpers';
import { useTranslation } from '@/contexts/TranslationContext';
import { useLocalizedNavigation } from '@/hooks/useLocalizedNavigation';

interface SEOHeadProps {
  title?: string;
  description?: string;
  image?: string;
  type?: string;
  noIndex?: boolean;
  noFollow?: boolean;
  canonicalUrl?: string;
  keywords?: string[];
  schemaData?: any;
}

export function SEOHead({
  title: propTitle,
  description: propDescription,
  image: propImage,
  type = 'website',
  noIndex,
  noFollow,
  canonicalUrl,
  keywords,
  schemaData
}: SEOHeadProps) {
  const location = useLocation();
  const { seoSettings, getPageSEOByPath, getCustomTagsForPage } = useSEO();
  const pageDefaults = usePageSEODefaults(location.pathname);
  const { locale } = useTranslation();
  const { getCurrentPath, getFullAlternateUrl } = useLocalizedNavigation();

  const currentPath = location.pathname;
  const cleanPath = getCurrentPath(); // Path without locale prefix
  const pageSEO = getPageSEOByPath(currentPath);
  const customTags = getCustomTagsForPage(currentPath);

  // Determine final values with enhanced fallback priority and i18n support
  const finalTitle = propTitle || pageSEO?.title || pageDefaults.title;
  const finalDescription = propDescription || pageSEO?.meta_description || pageDefaults.description;
  const finalImage = propImage || pageSEO?.og_image || seoSettings?.og_image;
  
  // Generate canonical URL with language context
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const defaultCanonicalUrl = locale === 'pt-BR' 
    ? `${baseUrl}${cleanPath}`
    : `${baseUrl}/en${cleanPath === '/' ? '' : cleanPath}`;
  const finalCanonical = canonicalUrl || pageSEO?.canonical_url || defaultCanonicalUrl;
  
  const finalKeywords = keywords || pageSEO?.keywords || pageDefaults.keywords || [];
  const shouldNoIndex = noIndex || pageSEO?.no_index || false;
  const shouldNoFollow = noFollow || pageSEO?.no_follow || false;

  // Generate final title with template
  const formattedTitle = seoSettings?.title_template 
    ? seoSettings.title_template.replace('{title}', finalTitle)
    : finalTitle;

  useEffect(() => {
    // Update document title
    document.title = formattedTitle;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      if (!content) return;
      
      const attribute = isProperty ? 'property' : 'name';
      const selector = `meta[${attribute}="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    const updateLinkTag = (rel: string, href: string) => {
      if (!href) return;
      
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    };

    // Basic meta tags
    updateMetaTag('description', finalDescription);
    if (finalKeywords.length > 0) {
      updateMetaTag('keywords', finalKeywords.join(', '));
    }

    // Robots meta tag
    if (shouldNoIndex || shouldNoFollow) {
      const robotsContent = [
        shouldNoIndex ? 'noindex' : 'index',
        shouldNoFollow ? 'nofollow' : 'follow'
      ].join(', ');
      updateMetaTag('robots', robotsContent);
    }

    // Open Graph with i18n support
    updateMetaTag('og:title', pageSEO?.og_title || finalTitle, true);
    updateMetaTag('og:description', pageSEO?.og_description || finalDescription, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', finalCanonical, true);
    updateMetaTag('og:locale', locale, true);
    
    // Add alternate locale for OpenGraph
    const alternateLocale = locale === 'pt-BR' ? 'en' : 'pt-BR';
    updateMetaTag('og:locale:alternate', alternateLocale, true);
    
    if (finalImage) {
      updateMetaTag('og:image', finalImage, true);
    }
    if (seoSettings?.site_name) {
      updateMetaTag('og:site_name', seoSettings.site_name, true);
    }

    // Twitter Cards
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', pageSEO?.twitter_title || finalTitle);
    updateMetaTag('twitter:description', pageSEO?.twitter_description || finalDescription);
    if (pageSEO?.twitter_image || finalImage) {
      updateMetaTag('twitter:image', pageSEO?.twitter_image || finalImage);
    }
    if (seoSettings?.twitter_handle) {
      updateMetaTag('twitter:site', seoSettings.twitter_handle);
      updateMetaTag('twitter:creator', seoSettings.twitter_handle);
    }

    // Canonical URL
    updateLinkTag('canonical', finalCanonical);

    // Add hreflang links for internationalization
    const addHrefLangLinks = () => {
      // Remove existing hreflang links
      const existingHreflangs = document.querySelectorAll('link[hreflang]');
      existingHreflangs.forEach(link => link.remove());

      // Add hreflang for Portuguese (default)
      const ptHreflangLink = document.createElement('link');
      ptHreflangLink.rel = 'alternate';
      ptHreflangLink.hreflang = 'pt-BR';
      ptHreflangLink.href = `${baseUrl}${cleanPath}`;
      document.head.appendChild(ptHreflangLink);

      // Add hreflang for English
      const enHreflangLink = document.createElement('link');
      enHreflangLink.rel = 'alternate';
      enHreflangLink.hreflang = 'en';
      enHreflangLink.href = `${baseUrl}/en${cleanPath === '/' ? '' : cleanPath}`;
      document.head.appendChild(enHreflangLink);

      // Add x-default hreflang (defaults to Portuguese)
      const xDefaultLink = document.createElement('link');
      xDefaultLink.rel = 'alternate';
      xDefaultLink.hreflang = 'x-default';
      xDefaultLink.href = `${baseUrl}${cleanPath}`;
      document.head.appendChild(xDefaultLink);
    };

    addHrefLangLinks();

    // Structured Data (Schema.org) - Enhanced with new helpers
    const removeExistingSchema = () => {
      const existingSchemas = document.querySelectorAll('script[type="application/ld+json"]');
      existingSchemas.forEach(script => {
        if (script.getAttribute('data-seo-component')) {
          script.remove();
        }
      });
    };

    const addSchemaData = (data: any) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-component', 'true');
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    };

    removeExistingSchema();

    // Generate and add comprehensive schemas using new helpers
    const schemas = generatePageSchema(currentPath, seoSettings, { 
      title: finalTitle, 
      description: finalDescription 
    });
    
    schemas.forEach(schema => {
      addSchemaData(schema);
    });

    // Add custom schema from props (legacy support)
    if (schemaData) {
      addSchemaData(schemaData);
    }

    // Add performance hints
    const performanceHints = getPerformanceHints(currentPath);
    
    // Add preconnect hints
    performanceHints.preconnect.forEach(url => {
      let link = document.querySelector(`link[rel="preconnect"][href="${url}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = url;
        if (url.includes('fonts.gstatic.com')) {
          link.crossOrigin = 'anonymous';
        }
        document.head.appendChild(link);
      }
    });

    // Add prefetch hints
    performanceHints.prefetch.forEach(url => {
      let link = document.querySelector(`link[rel="prefetch"][href="${url}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url;
        document.head.appendChild(link);
      }
    });

    // Handle custom tags
    customTags.forEach(tag => {
      if (tag.tag_type === 'head' && tag.is_active) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = tag.content;
        const elements = Array.from(tempDiv.children);
        
        elements.forEach(element => {
          element.setAttribute('data-custom-tag', tag.id);
          document.head.appendChild(element);
        });
      }
    });

    // Cleanup function to remove custom tags
    return () => {
      customTags.forEach(tag => {
        const customElements = document.querySelectorAll(`[data-custom-tag="${tag.id}"]`);
        customElements.forEach(el => el.remove());
      });
    };
  }, [
    formattedTitle,
    finalDescription,
    finalImage,
    finalCanonical,
    finalKeywords,
    shouldNoIndex,
    shouldNoFollow,
    type,
    pageSEO,
    seoSettings,
    currentPath,
    schemaData,
    customTags
  ]);

  // Enhanced Analytics and Tag Manager integration
  useEffect(() => {
    if (!seoSettings?.google_analytics_id && !seoSettings?.google_tag_manager_id && 
        !seoSettings?.facebook_pixel_id && !seoSettings?.linkedin_partner_id) return;

    // Initialize dataLayer
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
    }

    // Google Tag Manager (Primary - handles GA4, Meta Pixel, LinkedIn via GTM)
    if (seoSettings.google_tag_manager_id) {
      const gtmScript = document.createElement('script');
      gtmScript.innerHTML = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${seoSettings.google_tag_manager_id}');
        
         // Enhanced dataLayer with site info and i18n
         window.dataLayer.push({
           'site_name': 'Guilds',
           'site_version': '2.0',
           'page_type': '${getPageType(location.pathname)}',
           'user_type': 'visitor',
           'page_language': '${locale}',
           'content_group1': '${locale}',
           'content_group2': '${getPageType(location.pathname)}'
         });
      `;
      document.head.appendChild(gtmScript);

      const gtmNoScript = document.createElement('noscript');
      gtmNoScript.innerHTML = `
        <iframe src="https://www.googletagmanager.com/ns.html?id=${seoSettings.google_tag_manager_id}"
        height="0" width="0" style="display:none;visibility:hidden"></iframe>
      `;
      document.body.appendChild(gtmNoScript);
    }

    // Direct GA4 (fallback if no GTM)
    if (seoSettings.google_analytics_id && !seoSettings.google_tag_manager_id) {
      const gtagScript = document.createElement('script');
      gtagScript.async = true;
      gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${seoSettings.google_analytics_id}`;
      document.head.appendChild(gtagScript);

      const gtagInlineScript = document.createElement('script');
      gtagInlineScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
         gtag('config', '${seoSettings.google_analytics_id}', {
           page_title: document.title,
           page_location: window.location.href,
           send_page_view: true,
           language: '${locale}',
           content_group1: '${locale}',
           content_group2: '${getPageType(location.pathname)}',
           custom_map: {
             'custom_parameter_1': 'page_type',
             'custom_parameter_2': 'user_type',
             'custom_parameter_3': 'page_language'
           }
         });
      `;
      document.head.appendChild(gtagInlineScript);
    }

    // Direct Meta Pixel (fallback if no GTM)
    if (seoSettings.facebook_pixel_id && !seoSettings.google_tag_manager_id) {
      const fbPixelScript = document.createElement('script');
      fbPixelScript.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${seoSettings.facebook_pixel_id}');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(fbPixelScript);

      const fbPixelNoScript = document.createElement('noscript');
      fbPixelNoScript.innerHTML = `
        <img height="1" width="1" style="display:none"
        src="https://www.facebook.com/tr?id=${seoSettings.facebook_pixel_id}&ev=PageView&noscript=1" />
      `;
      document.head.appendChild(fbPixelNoScript);
    }

    // Direct LinkedIn Insight Tag (fallback if no GTM)
    if (seoSettings.linkedin_partner_id && !seoSettings.google_tag_manager_id) {
      const linkedinScript = document.createElement('script');
      linkedinScript.innerHTML = `
        _linkedin_partner_id = "${seoSettings.linkedin_partner_id}";
        window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
        window._linkedin_data_partner_ids.push(_linkedin_partner_id);
      `;
      document.head.appendChild(linkedinScript);

      const linkedinInsightScript = document.createElement('script');
      linkedinInsightScript.async = true;
      linkedinInsightScript.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
      document.head.appendChild(linkedinInsightScript);

      const linkedinNoScript = document.createElement('noscript');
      linkedinNoScript.innerHTML = `
        <img height="1" width="1" style="display:none" alt=""
        src="https://px.ads.linkedin.com/collect/?pid=${seoSettings.linkedin_partner_id}&fmt=gif" />
      `;
      document.head.appendChild(linkedinNoScript);
    }

    // Cleanup function
    return () => {
      const scripts = document.querySelectorAll(
        'script[src*="googletagmanager"], script[src*="gtag"], script[src*="fbevents"], script[src*="linkedin"]'
      );
      scripts.forEach(script => script.remove());
      
      const noscripts = document.querySelectorAll('noscript');
      noscripts.forEach(noscript => {
        if (noscript.innerHTML.includes('googletagmanager') || 
            noscript.innerHTML.includes('facebook.com/tr') ||
            noscript.innerHTML.includes('linkedin.com')) {
          noscript.remove();
        }
      });
    };
  }, [seoSettings?.google_analytics_id, seoSettings?.google_tag_manager_id, 
      seoSettings?.facebook_pixel_id, seoSettings?.linkedin_partner_id, location.pathname, locale]);

  // Helper function to determine page type
  const getPageType = (pathname: string): string => {
    if (pathname === '/') return 'home';
    if (pathname.startsWith('/lab')) return 'workshop';
    if (pathname.startsWith('/craft')) return 'innovation';
    if (pathname.includes('software') || pathname.includes('automacao') || pathname.includes('jogos')) return 'service';
    if (pathname === '/sobre' || pathname === '/equipe') return 'about';
    if (pathname === '/contato') return 'contact';
    if (pathname === '/carreiras') return 'careers';
    return 'other';
  };

  return null; // This component only manages head elements
}